import { useTranslationMipManage } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks.tsx';
import { TEST_ELEMENT_IDS } from '@dealstack/shared/tests/testElementIds.ts';
import { Button, Flex, Text, Textarea } from '@mantine/core';

const REASON_MAX_LENGTH = 1000;

interface Props {
	reason: string;
	reasonError: string;
	disabled: boolean;
	onReasonChange: (next: string) => void;
	onCancel: () => void;
	onConfirm: () => void;
}

/**
 * The reason a decline is given, revealed under the decision buttons — the same flow the
 * KYC reviewer follows. The account holder is emailed what is typed here.
 */
export const DeclineReasonPanel = ({
	reason,
	reasonError,
	disabled,
	onReasonChange,
	onCancel,
	onConfirm,
}: Props) => {
	const tMipManage = useTranslationMipManage();

	return (
		<Flex direction="column" gap="0.75rem" px="1rem" pb="1rem">
			<Text size="sm" c="dark.0">
				{tMipManage('bank_accounts.review.decline_reason.hint')}
			</Text>

			<Flex justify="space-between" align="center">
				<Text size="sm" fw={600} c="dark.8">
					{tMipManage('bank_accounts.review.decline_reason.label')}
				</Text>
				<Text size="xs" c="gray.7">
					{reason.length}/{REASON_MAX_LENGTH}
				</Text>
			</Flex>

			<Textarea
				value={reason}
				onChange={(event) => onReasonChange(event.target.value)}
				placeholder={tMipManage('bank_accounts.review.decline_reason.placeholder')}
				error={reasonError}
				disabled={disabled}
				maxLength={REASON_MAX_LENGTH}
				autosize
				minRows={4}
				maxRows={8}
				data-testid={TEST_ELEMENT_IDS.MIP_MANAGE_BANK_ACCOUNT_DECLINE_REASON_INPUT}
			/>

			<Flex gap="0.5rem">
				<Button variant="default" fullWidth onClick={onCancel} disabled={disabled}>
					{tMipManage('bank_accounts.review.decline_reason.cancel')}
				</Button>
				<Button
					fullWidth
					color="red"
					loading={disabled}
					onClick={onConfirm}
					data-testid={TEST_ELEMENT_IDS.MIP_MANAGE_BANK_ACCOUNT_DECLINE_CONFIRM_BUTTON}
				>
					{tMipManage('bank_accounts.review.decline_reason.confirm')}
				</Button>
			</Flex>
		</Flex>
	);
};
